import { Constants } from "appConstants";
import { BaseForm, Input, Loading, Select, TextAreaInput } from "components";
import DynamicSelectTextInputGroup from "components/DynamicSelectTextInputGroup";
import { ErrorHelper } from "helpers";
import useStandardToast from "hooks/useStandardToast";
import { Tenant } from "models/tenant/Tenant";
import { TenantType } from "models/tenant/TenantType";
import { TenantFields } from "models/tenant/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TenantService, TenantTypeService } from "services";
import * as Yup from "yup";
import "./styles.scss";

export interface TenantFormProps {
	isEditForm: boolean;
}

const emptyTenant = {
	id: "",
	name: "",
	type: "",
	typeId: "",
	description: "",
	url: "",
	homologUrl: "",
	backofficeUrl: ""
};

export default function TenantForm(props: Readonly<TenantFormProps>) {
	const { isEditForm } = props;
	const { showErrorToast, showSuccessToast } = useStandardToast();

	const { id } = useParams();

	const navigate = useNavigate();

	const [tenant, setTenant] = useState<Tenant>(new Tenant(emptyTenant));
	const [tenantTypes, setTenantTypes] = useState<TenantType[]>([]);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const tenantService = useMemo(() => new TenantService(), []);
	const tenantTypeService = useMemo(() => new TenantTypeService(), []);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				name: Yup.string().required(Constants.strings.inputs.requiredField),
				typeId: Yup.string().required(Constants.strings.inputs.requiredField),
				description: Yup.string().nullable(),
				url: Yup.string().nullable(),
				homologUrl: Yup.string().nullable(),
				backofficeUrl: Yup.string().nullable()
			}),
		[]
	);

	const linkOptions = useMemo(() => {
		return [
			{
				label: Constants.strings.pages.newTenant.url.label,
				value: TenantFields.URL,
				text: ""
			},
			{
				label: Constants.strings.pages.newTenant.homologUrl.label,
				value: TenantFields.HOMOLOGATION,
				text: ""
			},
			{
				label: Constants.strings.pages.newTenant.backofficeUrl.label,
				value: TenantFields.BACKOFFICE,
				text: ""
			}
		];
	}, []);

	const onSubmit = useCallback(
		(data: any) => {
			setIsSubmitting(true);

			const parsedData = { ...data };
			Object.keys(parsedData).forEach((key) => {
				if (parsedData[key] === "") {
					delete parsedData[key];
				}
			});

			const tenantPromise = isEditForm
				? tenantService.editTenant(id as string, parsedData)
				: tenantService.createTenant(parsedData);

			const operationSucessTitle = isEditForm
				? Constants.strings.success.tenantUpdatedTitle
				: Constants.strings.success.tenantAddedTitle;

			const operationSucessMessage = isEditForm
				? Constants.strings.success.tenantUpdatedMessage(
						data[TenantFields.NAME]
				  )
				: Constants.strings.success.tenantAddedMessage(data[TenantFields.NAME]);

			tenantPromise
				.then(() => {
					navigate("/backoffice/tenants");
					showSuccessToast(operationSucessMessage, operationSucessTitle);
				})
				.catch((error) => {
					showErrorToast(ErrorHelper.getErrorMessage(error));
				})
				.finally(() => {
					setIsSubmitting(false);
				});
		},
		[tenantService]
	);

	const fetchTenantTypes = useCallback(() => {
		tenantTypeService
			.getList()
			.then((response: any[]) => {
				setTenantTypes(response);
			})
			.catch(() => {
				showErrorToast(Constants.strings.errors.failedToGetTenantTypes);
				setTenantTypes([]);
			});
	}, []);

	const fetchTenant = useCallback(() => {
		setIsLoading(true);
		tenantService
			.getTenantDetails(String(id))
			.then((tenantResponse: Tenant) => {
				setTenant(tenantResponse);
			})
			.catch(() => {
				navigate("/backoffice/tenants");
				showErrorToast(Constants.strings.errors.failedToGetTenantDetails);
			})
			.finally(() => setIsLoading(false));
	}, [id]);

	useEffect(() => {
		fetchTenantTypes();
		if (isEditForm && id) fetchTenant();
	}, []);

	return isLoading ? (
		<div className="tenant-form-loading">
			<Loading type="primary" size={75} />
		</div>
	) : (
		<BaseForm
			initialValues={tenant}
			isEditForm={isEditForm}
			isSubmitting={isSubmitting}
			onSubmit={onSubmit}
			validationSchema={validationSchema}
			title={
				isEditForm
					? tenant?.[TenantFields.NAME]
					: Constants.strings.pages.newTenant.newProduct
			}
		>
			{(formik) => {
				const initialDisplayedFields = linkOptions.filter(
					(item) => formik.values[item?.value]
				);
				if (initialDisplayedFields.length === 0) {
					initialDisplayedFields.push(linkOptions[0]);
				}
				return (
					<>
						<p className="text-lg font-semibold mb-2">
							{Constants.strings.pages.newTenant.tenantData}
						</p>
						<Input
							name="name"
							label={Constants.strings.pages.newTenant.name}
							placeholder={Constants.strings.pages.newTenant.name}
							type="text"
							value={formik.values.name}
							onChange={formik.handleChange}
							error={formik.errors.name as string}
							touched={!!formik.touched.name}
							required
						/>
						<Select
							name="typeId"
							label={Constants.strings.pages.newTenant.tenantType.label}
							placeholder={
								Constants.strings.pages.newTenant.tenantType.placeholder
							}
							options={tenantTypes.map((type) => ({
								label: type.name,
								value: type.id
							}))}
							value={formik.values.typeId}
							getOptionValue={(option) => option.value}
							setFieldValue={formik.setFieldValue}
							error={formik.errors.typeId as string}
							touched={!!formik.touched.typeId}
							required
							isClearable={false}
						/>
						<TextAreaInput
							name="description"
							label={Constants.strings.pages.newTenant.description.label}
							placeholder={
								Constants.strings.pages.newTenant.description.placeholder
							}
							value={formik.values.description ?? ""}
							error={formik.errors.description as string}
							touched={!!formik.touched.description}
							onChange={formik.handleChange}
							maxLength={255}
						/>
						<p className="text-lg font-semibold mb-2">
							{Constants.strings.pages.newTenant.accessLinks}
						</p>
						<DynamicSelectTextInputGroup
							name="accessLinks"
							isRequired
							labelSelect={Constants.strings.pages.newTenant.linkType}
							labelInput={Constants.strings.pages.newTenant.link}
							maxFields={3}
							placeholders={{
								[TenantFields.URL]: "https://",
								[TenantFields.HOMOLOGATION]: "https://",
								[TenantFields.BACKOFFICE]: "https://"
							}}
							initialFields={initialDisplayedFields}
							minFields={initialDisplayedFields.length}
							options={linkOptions}
							setFieldValue={formik.setFieldValue}
							onBlur={formik.handleBlur}
							values={{
								url: formik.values?.url,
								homologUrl: formik.values?.homologUrl,
								backofficeUrl: formik.values?.backofficeUrl
							}}
						/>
					</>
				);
			}}
		</BaseForm>
	);
}
